<template>
  <NotFoundPage v-if="isNotFound" :reason="isNotFound" />
  <div v-else>
    <Breadcrumb :breadcrumb="breadcrumbData" id="breadcrumb" />
    <div class="set-page container">
      <ProductSet v-if="set" :set="set" :promotion="currentPromotion.name"></ProductSet>
      <Divider margin-top="var(--space-10)" />
      <div class="all-sets-container">
        <h2 class="header-default light">
          More <template v-if="roomName"> {{ roomName }}</template> Bundles
        </h2>
        <div class="all-sets-message">
          {{ roomTagline }}
          <a :href="shopUrl" class="accent">
            Shop all
            <template v-if="roomName"> {{ roomName }}</template> Bundles </a
          >.
        </div>
        <div>
          <ProductSetTiles v-if="roomName" :sets="relatedSets" :mobile-breakpoint="1200" :large-images="true" />
        </div>
        <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
        <WhyBuyFromArticle />
      </div>
    </div>
  </div>
</template>
<script>
import { Divider } from '@/pixel/index';
import {
  computed, //
  watch,
  ref,
  defineAsyncComponent
} from 'vue';

import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import WhyBuyFromArticle from '@/components/commons/WhyBuyFromArticle.vue';
import ProductSet from '@/components/sets/ProductSet.vue';
import useProductBundle from '@/compositions/useProductBundle.js';
import { useRouter } from 'vue-router';
import ProductSetTiles from '@/components/sets/ProductSetTiles.vue';
import useCommon from '@/compositions/useCommon';
import useHead from '@/compositions/useHead';
import useStatusCode from '@/compositions/useStatusCode';

export default {
  name: 'SetPage',
  components: {
    Divider,
    ProductSet,
    WhyBuyFromArticle,
    ProductSetTiles,
    Breadcrumb,
    NotFoundPage: defineAsyncComponent(() => import('@/pages/Errors/NotFoundPage.vue')),
  },

  async setup() {
    const isNotFound = ref('');
    const { currentPromotion } = useCommon();
    const { redirectPermanent } = useStatusCode();
    const router = useRouter();
    const setId = router.currentRoute.value.params?.id ?? null;
    const set = ref({});
    const relatedSets = ref([]);
    const roomName = computed(() => {
      if (set.value.productBundleRooms?.length === 1) {
        return set.value.productBundleRooms[0];
      }

      return null;
    });
    const shopUrl = computed(() => {
      const url = '/furniture-bundles';

      if (roomName.value) {
        return `${url}?filter.rooms=${roomName.value}`;
      }

      return url;
    });

    const { loadProductBundles, loadProductBundle, loadProductBundleRoomTagline } = useProductBundle();

    async function loadSet() {
      const result = await loadProductBundle(setId);

      set.value = result?.productBundle ?? {};
    }

    useHead(
      {
        title: computed(() => `${set.value?.title} | Article`),
      },
      { appendTitleSuffix: false },
    );

    async function loadRelatedSets() {
      const result = await loadProductBundles({
        page: 0, //
        count: 3,
        rooms: [roomName.value],
        except: Number(set.value.id),
      });
      relatedSets.value = result?.productBundles || [];
    }

    const roomTagline = computed(() => {
      if (roomName.value) {
        return loadProductBundleRoomTagline(roomName.value);
      }
      return null;
    });

    watch(roomName, () => loadRelatedSets());

    const breadcrumbData = computed(() => {
      return [
        {
          title: 'Home',
          url: import.meta.env.VITE_STOREFRONT_BASE_URL,
        },
        {
          title: 'Furniture Bundles',
          url: `${import.meta.env.VITE_STOREFRONT_BASE_URL}${router.resolve({ name: 'sets' }).href}`,
        },
        {
          title: `${roomName.value || ''} Bundles`,
          url: `${import.meta.env.VITE_STOREFRONT_BASE_URL}/furniture-bundles?filter.rooms=${roomName.value}`,
        },
        {
          title: set.value?.title,
          url: `${import.meta.env.VITE_STOREFRONT_BASE_URL}${router.currentRoute.value.path}`,
        },
      ];
    });

    await loadSet();

    if (!set.value.id) {
      isNotFound.value = 'Furniture bundle not found';
    }else if (set.value.slug !== router.currentRoute.value.params?.slug) {
      redirectPermanent(
        router.resolve({ name: 'set', params: { id: set.value.id, slug: set.value.slug } }).href,
        `The provided set slug (${router.currentRoute.value.params.slug}) is different to the expected set slug (${set.value.slug}).`,
      );
    }
  

    await loadRelatedSets();

    return {
      set,
      roomName,
      currentPromotion,
      shopUrl,
      roomTagline,
      relatedSets,
      breadcrumbData,
      isNotFound
    };
  },
};
</script>
<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.set-page {
  margin-left: auto;
  margin-right: auto;

  .all-sets-container {
    width: 100%;
    margin-top: var(--space-10);
    .all-sets-message {
      margin-top: var(--space-4);
      margin-bottom: var(--space-6);
    }

    /* purgecss ignore */
    ::v-deep(.productbundle-items-grid .flex-grid) {
      @media (min-width: $mobile-breakpoint) {
        grid-template-columns: 1fr 1fr 1fr;
        gap: var(--space-6);
        @media (min-width: $desktop-breakpoint) {
          gap: var(--space-10);
        }
      }
    }

    .productbundle-items-grid {
      padding: 0;
    }
  }
}
</style>
